import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, Table, Badge } from 'react-bootstrap';

const PAGE_SIZE = 5;

const Leaderboard = ({ players, showAnswer = false }) => {
  const [tab, setTab] = React.useState('overall'); // 'overall' | 'question'
  const [page, setPage] = React.useState(0);

  // Soru bitince (cevap gösterilince) otomatik "Bu Soru" sekmesine geç;
  // yeni soru başlayınca "Genel"e dön.
  React.useEffect(() => {
    if (showAnswer) {
      setTab('question');
    } else {
      setTab('overall');
    }
    setPage(0);
  }, [showAnswer]);

  // Sekme değişince başa dön
  React.useEffect(() => { setPage(0); }, [tab]);

  // Sıralı liste (aktif sekmeye göre)
  const sorted = React.useMemo(() => {
    const arr = [...(players || [])];
    if (tab === 'question') {
      // "Bu Soru" (A tipi): herkes listede; o soruda kazanılan puana göre azalan,
      // 0 alanlar / cevaplamayanlar en altta.
      return arr.sort((a, b) => (b.lastQuestionPoints || 0) - (a.lastQuestionPoints || 0));
    }
    return arr.sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [players, tab]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const startIdx = safePage * PAGE_SIZE;
  const pageItems = sorted.slice(startIdx, startIdx + PAGE_SIZE);

  const getRankStyle = (globalIdx) => {
    switch (globalIdx) {
      case 0: return { bg: 'warning', text: 'white', icon: '🥇' };
      case 1: return { bg: 'secondary', text: 'white', icon: '🥈' };
      case 2: return { bg: 'danger', text: 'white', icon: '🥉' };
      default: return { bg: 'light', text: 'dark', icon: `#${globalIdx + 1}` };
    }
  };

  return (
    <Card className="custom-card border-0 overflow-hidden">
      <div className="bg-primary p-3 text-white d-flex align-items-center gap-2">
        <Trophy size={20} className="text-warning" />
        <h6 className="mb-0 fw-bold">Liderlik Tablosu</h6>
      </div>

      {/* Sekmeler */}
      <div className="d-flex border-bottom">
        <button
          type="button"
          onClick={() => setTab('overall')}
          className={`flex-fill btn btn-sm rounded-0 fw-bold py-2 border-0 ${
            tab === 'overall' ? 'text-primary bg-primary bg-opacity-10' : 'text-muted'
          }`}
        >
          Genel
        </button>
        <button
          type="button"
          onClick={() => setTab('question')}
          className={`flex-fill btn btn-sm rounded-0 fw-bold py-2 border-0 d-flex align-items-center justify-content-center gap-1 ${
            tab === 'question' ? 'text-primary bg-primary bg-opacity-10' : 'text-muted'
          }`}
        >
          <Zap size={14} /> Bu Soru
        </button>
      </div>

      <Table hover className="mb-0 align-middle">
        <tbody>
          {pageItems.map((player, idx) => {
            const globalIdx = startIdx + idx;
            const style = getRankStyle(globalIdx);
            const qPoints = player.lastQuestionPoints || 0;
            const qMult = player.lastQuestionMultiplier || 1;
            const qBase = player.lastQuestionBase || 0;
            const answered = !!player.lastQuestionAnswered;

            return (
              <motion.tr
                key={player.id || player.nickname}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={globalIdx === 0 ? 'bg-warning bg-opacity-10' : ''}
              >
                <td className="ps-3" style={{ width: 50 }}>
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center fw-bold bg-${style.bg} text-${style.text}`}
                    style={{ width: 32, height: 32 }}
                  >
                    {globalIdx > 2 ? style.icon : <span style={{ fontSize: '1.2rem' }}>{style.icon}</span>}
                  </div>
                </td>
                <td className="fw-bold text-dark">{player.nickname || player.name}</td>

                {tab === 'question' ? (
                  <td className="text-end pe-3">
                    {!answered ? (
                      <span className="text-muted small fst-italic">Cevap yok</span>
                    ) : qPoints > 0 ? (
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        {qMult > 1 && (
                          <Badge bg="danger" className="rounded-pill d-flex align-items-center gap-1">
                            <Zap size={11} fill="white" /> {qMult}x
                          </Badge>
                        )}
                        <span className="fw-bold font-monospace text-success">
                          {qMult > 1 && (
                            <span className="text-muted me-1 fw-normal" style={{ fontSize: '0.75rem' }}>
                              {qBase}→
                            </span>
                          )}
                          +{qPoints}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted small">0 P</span>
                    )}
                  </td>
                ) : (
                  <td className="text-end pe-3 fw-bold font-monospace text-primary">
                    {player.score} P
                  </td>
                )}
              </motion.tr>
            );
          })}

          {pageItems.length === 0 && (
            <tr>
              <td colSpan="3" className="text-center py-4 text-muted small">
                {tab === 'question' ? 'Bu soruda henüz veri yok.' : 'Henüz veri yok.'}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Sayfalama (offset) */}
      {sorted.length > PAGE_SIZE && (
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top bg-light">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary rounded-pill px-2 py-0 d-flex align-items-center"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="small text-secondary fw-bold">
            {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, sorted.length)} / {sorted.length}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary rounded-pill px-2 py-0 d-flex align-items-center"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </Card>
  );
};

export default Leaderboard;
